import React, { useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import styles from './Drawer.module.css';

export type DrawerSize = 'sm' | 'md' | 'lg';
export type DrawerPlacement = 'right' | 'left';

export interface DrawerProps {
  open: boolean;
  onClose: () => void;
  /** Text rendered in the header */
  title?: React.ReactNode;
  /** Caption under the title, for context the panel does not repeat */
  subtitle?: React.ReactNode;
  /** Content rendered below the header */
  children?: React.ReactNode;
  /** Content rendered in the footer (e.g. action buttons) */
  footer?: React.ReactNode;
  size?: DrawerSize;
  placement?: DrawerPlacement;
  /**
   * Dim and block the page behind the panel.
   *
   * Defaults to **false**, which is the whole difference from `Modal`: a
   * drawer exists so that what it edits stays readable *and* clickable
   * while it is open. Only `modal` renders a backdrop or locks body
   * scroll.
   */
  modal?: boolean;
  /** Whether Escape closes the drawer. Defaults to true. */
  closeOnEscape?: boolean;
  /** Whether to show the × close button in the header. Defaults to true. */
  showCloseButton?: boolean;
  /**
   * Keep children mounted while closed, mirroring `TabPanel`. Off by
   * default: a closed drawer's fields should come back at their stored
   * values rather than holding stale local state.
   */
  keepMounted?: boolean;
  className?: string;
  'data-testid'?: string;
}

/**
 * A side sheet for editing something while it stays on screen.
 *
 * Reach for this over `Modal` when the content behind matters — a live
 * chart being calibrated, say. `Modal` always paints a fixed backdrop over
 * the page and locks body scroll; this renders **no backdrop element at
 * all** unless `modal` is set, so nothing can intercept a click aimed at
 * the page behind.
 *
 * Deliberately does **not** trap focus: being able to Tab or click out to
 * the content you are editing is the point.
 */
const Drawer: React.FC<DrawerProps> = ({
  open,
  onClose,
  title,
  subtitle,
  children,
  footer,
  size = 'sm',
  placement = 'right',
  modal = false,
  closeOnEscape = true,
  showCloseButton = true,
  keepMounted = false,
  className,
  'data-testid': dataTestId,
}) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<Element | null>(null);

  const handleBackdropClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (modal && event.target === event.currentTarget) onClose();
    },
    [modal, onClose]
  );

  useEffect(() => {
    if (!open || !closeOnEscape) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      // A Modal opened on top of this drawer owns the key. Both attach a
      // document-level listener, so without this one Escape would close
      // the drawer underneath as well.
      if (document.querySelector('[aria-modal="true"]')) return;
      onClose();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, closeOnEscape, onClose]);

  // Body scroll stays free unless this is a blocking drawer -- the page
  // behind is meant to remain usable.
  useEffect(() => {
    if (!open || !modal) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open, modal]);

  // Focus the panel on open and hand focus back on close. These drawers
  // are opened repeatedly from the same buttons, so returning focus is
  // what keeps the keyboard path usable.
  useEffect(() => {
    if (!open) return;
    previouslyFocused.current = document.activeElement;
    panelRef.current?.focus();
    return () => {
      const target = previouslyFocused.current as HTMLElement | null;
      if (target && typeof target.focus === 'function') target.focus();
    };
  }, [open]);

  if (!open && !keepMounted) return null;

  return createPortal(
    <div
      className={[
        styles.root,
        styles[placement],
        modal ? styles.modal : null,
        !open ? styles.hidden : null,
      ]
        .filter(Boolean)
        .join(' ')}
      onClick={handleBackdropClick}
      hidden={!open}
    >
      <div
        ref={panelRef}
        tabIndex={-1}
        role="dialog"
        // Only claim modality when it is true. `aria-modal="false"` is the
        // spec default and saying it out loud confuses some tooling.
        aria-modal={modal ? 'true' : undefined}
        aria-label={typeof title === 'string' ? title : undefined}
        data-testid={dataTestId}
        className={[styles.panel, styles[size], className]
          .filter(Boolean)
          .join(' ')}
      >
        {(title != null || showCloseButton) && (
          <div className={styles.header}>
            <div className={styles.heading}>
              {title != null && <span className={styles.title}>{title}</span>}
              {subtitle != null && (
                <span className={styles.subtitle}>{subtitle}</span>
              )}
            </div>
            {showCloseButton && (
              <button
                type="button"
                className={styles.closeButton}
                onClick={onClose}
                aria-label="Close"
              >
                ×
              </button>
            )}
          </div>
        )}

        {children != null && <div className={styles.body}>{children}</div>}

        {footer != null && <div className={styles.footer}>{footer}</div>}
      </div>
    </div>,
    document.body
  );
};

Drawer.displayName = 'Drawer';

export { Drawer };
